import { Component, type ErrorInfo, type ReactNode } from 'react'
import i18n from 'i18next'
import { isChunkLoadError, shouldAutoReloadForChunkError } from '../utils/chunkError'

interface Props {
  children: ReactNode
  /** Смена ключа (например, pathname) сбрасывает состояние ошибки. */
  resetKey?: string
}

interface State {
  error: unknown
}

function sessionStorageOrNull(): Storage | null {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

/**
 * Перехватывает исключения рендера, чтобы вместо белого экрана показать
 * понятное сообщение. Сбой загрузки lazy-чанка после деплоя лечится одной
 * автоматической перезагрузкой (с защитой от цикла).
 *
 * Используется дважды: в main.tsx (корень — ловит всё, включая провайдеры)
 * и в App.tsx вокруг маршрутов (resetKey = pathname: переход на другую
 * страницу сбрасывает ошибку, оболочка приложения остаётся живой).
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: unknown): State {
    return { error }
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack)
    if (isChunkLoadError(error) && shouldAutoReloadForChunkError(sessionStorageOrNull())) {
      window.location.reload()
    }
  }

  override componentDidUpdate(prev: Props) {
    if (this.state.error != null && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null })
    }
  }

  override render() {
    const { error } = this.state
    if (error == null) return this.props.children
    const chunk = isChunkLoadError(error)
    return (
      <div role="alert" className="min-h-[50vh] flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-4">
          <h1 className="text-xl font-semibold text-gray-900">{i18n.t('errorBoundary.title')}</h1>
          <p className="text-gray-600">
            {chunk ? i18n.t('errorBoundary.chunkMessage') : i18n.t('errorBoundary.message')}
          </p>
          <div className="flex justify-center gap-3">
            <button
              type="button"
              className="px-4 py-2 rounded-lg bg-primary text-white"
              onClick={() => window.location.reload()}
            >
              {i18n.t('errorBoundary.reload')}
            </button>
            <a href="/" className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700">
              {i18n.t('errorBoundary.home')}
            </a>
          </div>
        </div>
      </div>
    )
  }
}
